import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Divider,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
} from "@mui/material";
import SidebarMenu from "../AdminPanel/SidebarMenu";
import TopBar from "../AdminPanel/TopBar";
import { useNavigate, useParams } from "react-router-dom";

const labelTipo = (tipo = "", origen = "") => {
  const o = String(origen || "").toUpperCase();
  const t = String(tipo || "").toUpperCase();
  if (o === "INVENTARIO") return t === "ING" ? "Ingreso" : t === "EGR" ? "Egreso" : "Inventario";
  if (o === "VENTA") return "Venta";
  if (o === "COMPRA") return "Compra";
  return o || "";
};

const MotionDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const [open, setOpen] = useState(false);
  const [submenuOpen, setSubmenuOpen] = useState(false);

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  const fetchDetail = async () => {
    setLoading(true);
    try {
      const r = await fetch(`/core/movimientos/${id}/`, { credentials: "include" });
      const d = await r.json().catch(() => null);
      setData(d && typeof d === "object" ? d : null);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDetail();
  }, [id]);

  const handleLogout = async () => {
    await fetch("/sistema/logout/", { method: "POST", credentials: "include" });
    localStorage.clear();
    navigate("/login");
  };

  const detalles = Array.isArray(data?.detalles) ? data.detalles : [];

  const totales = useMemo(() => {
    const totalCant = detalles.reduce((acc, it) => acc + (Number(it.cantidad) || 0), 0);
    const totalMonto = detalles.reduce((acc, it) => {
      const qty = Number(it.cantidad) || 0;
      const pu =
        Number(it.costo_unitario) ||
        Number(it.precio_unitario) ||
        Number(it.precio) ||
        0;
      const rowTotal = Number(it.total) || (qty * pu);
      return acc + (Number.isFinite(rowTotal) ? rowTotal : 0);
    }, 0);
    return { totalCant, totalMonto };
  }, [detalles]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", minHeight: "100vh", alignItems: "center", justifyContent: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!data) {
    return (
      <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: "#f5f6fa" }}>
        <Box sx={{ p: 4, width: "100%" }}>
          <Paper sx={{ p: 3, borderRadius: 2 }}>
            <Typography sx={{ fontWeight: 800, mb: 1 }}>No se pudo cargar el documento</Typography>
            <Typography sx={{ color: "#757575" }}>
              Revisa que el ID exista y que el endpoint responda correctamente.
            </Typography>
          </Paper>
        </Box>
      </Box>
    );
  }

  const tipoLabel = labelTipo(data.tipo, data.origen);

  const FieldRow = ({ label, value }) => (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "170px 1fr", sm: "220px 1fr" },
        gap: 2,
        py: 1.2,
        borderBottom: "1px solid #eee",
      }}
    >
      <Typography sx={{ fontSize: 12, color: "#757575", fontWeight: 700 }}>{label}</Typography>
      <Typography sx={{ color: "#212121", wordBreak: "break-word" }}>{value || "-"}</Typography>
    </Box>
  );

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: "#f5f6fa" }}>
      <SidebarMenu
        open={open}
        setOpen={setOpen}
        submenuOpen={submenuOpen}
        setSubmenuOpen={setSubmenuOpen}
        navigate={navigate}
        handleLogout={handleLogout}
      />

      <Box sx={{ flexGrow: 1 }}>
        <TopBar open={open} setOpen={setOpen} />

        <Box sx={{ width: "95%", mx: "auto", mt: 3, mb: 5 }}>
          <Paper sx={{ p: 3, borderRadius: 2, mb: 3 }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, flexWrap: "wrap" }}>
              <Box>
                <Typography sx={{ fontWeight: 900, fontSize: 18, lineHeight: 1.2 }}>
                  {data.codigo || "-"}
                </Typography>
                <Typography sx={{ fontSize: 12, color: "#757575", mt: 0.5 }}>
                  {String(data.origen || "").toUpperCase()}
                </Typography>
              </Box>

              <Chip
                label={tipoLabel}
                sx={{
                  fontWeight: 800,
                  bgcolor: "#E3F2FD",
                  color: "#1565C0",
                }}
              />
            </Box>

            <Divider sx={{ my: 2 }} />

            <Box sx={{ borderRadius: 1.5, overflow: "hidden", border: "1px solid #eee" }}>
              <FieldRow label="Fecha" value={data.fecha} />
              <FieldRow label="Responsable" value={data.responsable} />
              <FieldRow label="Referencia" value={data.referencia} />
              <FieldRow label="Estado" value={data.estado} />
              <FieldRow label="Descripción" value={data.descripcion} />
            </Box>
          </Paper>

          <Paper sx={{ p: 3, borderRadius: 2 }}>
            <Box sx={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 2, flexWrap: "wrap", mb: 2 }}>
              <Box>
                <Typography sx={{ fontWeight: 800 }}>Productos</Typography>
                <Typography sx={{ fontSize: 12, color: "#757575" }}>
                  {detalles.length ? `${detalles.length} ítems` : "Sin ítems"}
                </Typography>
              </Box>

              {detalles.length > 0 && (
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                  <Chip
                    label={`Cant.: ${totales.totalCant}`}
                    sx={{ fontWeight: 800, bgcolor: "#F3F6FF", color: "#1A237E" }}
                  />
                  <Chip
                    label={`Total: $${Number(totales.totalMonto || 0).toFixed(2)}`}
                    sx={{ fontWeight: 800, bgcolor: "#E8F5E9", color: "#1B5E20" }}
                  />
                </Box>
              )}
            </Box>

            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ bgcolor: "#1565C0", color: "#fff" }}>Código</TableCell>
                    <TableCell sx={{ bgcolor: "#1565C0", color: "#fff" }}>Producto</TableCell>
                    <TableCell sx={{ bgcolor: "#1565C0", color: "#fff" }} align="right">
                      Cant.
                    </TableCell>
                    <TableCell sx={{ bgcolor: "#1565C0", color: "#fff" }} align="right">
                      Precio Unit.
                    </TableCell>
                    <TableCell sx={{ bgcolor: "#1565C0", color: "#fff" }} align="right">
                      Total
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {detalles.map((it, idx) => {
                    const qty = Number(it.cantidad) || 0;
                    const unit =
                      Number(it.costo_unitario) ||
                      Number(it.precio_unitario) ||
                      Number(it.precio) ||
                      0;
                    const rowTotal = Number(it.total) || qty * unit;

                    return (
                      <TableRow key={idx} hover>
                        <TableCell>
                          {it.codigo || "-"}
                        </TableCell>

                        <TableCell
                          sx={{
                            maxWidth: 500,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                          title={it.producto || ""}
                        >
                          {it.producto || it.descripcion || "-"}
                        </TableCell>

                        <TableCell align="right" >
                          {qty}
                        </TableCell>

                        <TableCell align="right">
                          ${Number(unit || 0).toFixed(4)}
                        </TableCell>

                        <TableCell align="right">
                          ${Number(rowTotal || 0).toFixed(2)}
                        </TableCell>
                      </TableRow>
                    );
                  })}

                  {!detalles.length && (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        No hay productos en este documento
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Box>
      </Box>
    </Box>
  );
};

export default MotionDetail;
